import { fileUrl } from "@/lib/api";
import { FASES, FASE_AREA, fechaHora, fechaSolo } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { FASE_DOT } from "./constants";
import { Link } from "react-router-dom";
import { estadoHorma } from "@/lib/hormas";
import {
  Check, Paperclip, Lock, ExternalLink, Upload, FileDown, Target, Box, AlertTriangle,
} from "lucide-react";

export default function PasosList({
  pasosPorFase, puedeFase, toggleStep, triggerUpload,
  formatoDe, plantillaDe, versionesDe, fileRefs, fechaMeta, depurado, puedeDescargar = true,
  horma, hormaAnterior, bloqueoHorma, puedeElegirHorma, abrirHorma, ruta, abrirRuta,
}) {
  // Rutas (14-oct-2026): solo se muestran los pasos de la ruta del modelo. En las rutas sin pasos de horma,
  // la horma (del catálogo) es un dato del modelo y se muestra arriba, no como paso.
  const hormaEnPaso = ruta?.horma_en_paso !== false;
  // Paso 1 = elegir horma del catálogo; paso 2 sale de la horma. Los modelos que ya existían al publicar el menú
  // (o con el paso 1 hecho sin horma) siguen como antes: paso 1 a mano y su archivo de horma en el paso 2.
  const paso1Hecho = pasosPorFase("Desarrollo · Horma").some((p) => p.numero === 1 && p.hecho);
  const conHorma = !!horma;
  const comoAntes = !conHorma && (hormaAnterior || paso1Hecho);
  const pasoDeHorma = (p) => !comoAntes && (p.numero === 1 || p.numero === 2);
  const estH = estadoHorma(horma);
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="section-proceso">
      <div className="px-5 py-3.5 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-head font-bold tracking-tight">Proceso de desarrollo</h3>
          {abrirRuta && (
            <button onClick={abrirRuta} className="text-xs font-medium text-blue-600 hover:text-blue-700 shrink-0" data-testid="cambiar-ruta">Cambiar ruta</button>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-0.5">{ruta?.total ?? 24} compuertas agrupadas por fase. Los pasos con 📎 requieren documento.</p>
        <div className="mt-2 flex items-center gap-2 flex-wrap text-xs" data-testid="ruta-horma">
          <span className="inline-flex items-center gap-1 font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-2 py-0.5">
            Ruta: <span className="font-semibold text-slate-900">{ruta?.nombre || "Nuevo modelo (horma y suela nuevas)"}</span>
          </span>
          {!hormaEnPaso && (horma ? (
            <Link to={`/hormas/${horma.id}`} className="inline-flex items-center gap-1.5 font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-2 py-0.5 hover:border-slate-300">
              <Box size={12} className="text-slate-400" /> Horma: <span className="font-mono">{horma.codigo}</span> · {horma.nombre}
              <span className={`ml-1 text-[10px] font-semibold px-1.5 rounded-full ${estH.cls}`}>{estH.label}</span>
            </Link>
          ) : <span className="text-amber-700">Sin horma</span>)}
          {!hormaEnPaso && puedeElegirHorma && (
            <button onClick={abrirHorma} className="font-medium text-blue-600 hover:text-blue-700" data-testid="elegir-horma-ruta">{horma ? "Cambiar horma" : "Elegir horma"}</button>
          )}
        </div>
        {fechaMeta && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1" data-testid="pasos-fecha-meta">
            <Target size={12} className="text-slate-400" /> Fecha meta de prototipo: <span className="font-semibold text-slate-800">{fechaSolo(fechaMeta)}</span>
          </div>
        )}
        {bloqueoHorma && !depurado && (
          <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2" data-testid="bloqueo-horma">
            <AlertTriangle size={14} className="shrink-0 mt-px" /> {bloqueoHorma}
          </div>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {FASES.filter((fase) => pasosPorFase(fase).length > 0).map((fase) => {
          const pasos = pasosPorFase(fase);
          const puede = puedeFase(fase) && !depurado;
          return (
            <div key={fase} className="px-5 py-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: FASE_DOT[fase] }} />
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{FASE_AREA[fase]}</span>
              </div>
              <div className="space-y-2">
                {pasos.map((p) => {
                  const locked = p.requiere_documento && !p.documento_file_id && !pasoDeHorma(p);
                  const disabled = !puede || (locked && !p.hecho) || (!!bloqueoHorma && !p.hecho)
                    || pasoDeHorma(p);
                  return (
                    <div key={p.numero} className="py-1.5" data-testid={`paso-${p.numero}`}>
                      <div className="flex items-center gap-3">
                        <button
                          disabled={disabled}
                          onClick={() => toggleStep(p, !p.hecho)}
                          data-testid={`paso-check-${p.numero}`}
                          className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                            p.hecho ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300"
                          } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-emerald-400 cursor-pointer"}`}
                        >
                          {p.hecho && <Check size={13} strokeWidth={3} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[11px] font-mono text-slate-300">{p.orden ?? p.numero}</span>
                            <span className={`text-sm ${p.hecho ? "text-slate-800 font-medium" : "text-slate-600"}`}>{p.nombre}</span>
                            {p.requiere_documento && !pasoDeHorma(p) && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                                <Paperclip size={10} /> Documento
                              </span>
                            )}
                            {locked && <Lock size={12} className="text-amber-500" title="Sube el documento para habilitar" />}
                          </div>
                          {p.numero === 1 && (
                            <div className="flex items-center gap-2 flex-wrap mt-1" data-testid="paso1-horma">
                              {horma ? (
                                <Link to={`/hormas/${horma.id}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-2 py-0.5 hover:border-slate-300">
                                  <Box size={12} className="text-slate-400" /> <span className="font-mono">{horma.codigo}</span> · {horma.nombre}
                                  <span className={`ml-1 text-[10px] font-semibold px-1.5 rounded-full ${estH.cls}`}>{estH.label}</span>
                                </Link>
                              ) : !comoAntes ? (
                                <span className="text-[11px] text-slate-400">Elige una horma del catálogo (revisada o liberada) para iniciar el modelo.</span>
                              ) : null}
                              {puedeElegirHorma && (
                                <button onClick={abrirHorma} className="text-xs font-medium text-blue-600 hover:text-blue-700" data-testid="elegir-horma">
                                  {horma ? "Cambiar horma" : comoAntes ? "Ligar a horma del catálogo (opcional)" : "Elegir horma"}
                                </button>
                              )}
                            </div>
                          )}
                          {p.numero === 2 && pasoDeHorma(p) && (
                            <div className="text-[11px] text-slate-400 mt-0.5">
                              {conHorma ? <>Se toma de la horma: <Link to={`/hormas/${horma.id}`} className="text-blue-600 hover:text-blue-700">ver medidas y formato FDD-08</Link></> : "Se completa al elegir la horma."}
                            </div>
                          )}
                          {p.hecho && p.completado_en && (
                            <div className="text-[11px] text-slate-400 mt-0.5">✓ {fechaHora(p.completado_en)} · {p.completado_por_nombre}</div>
                          )}
                        </div>
                        {p.requiere_documento && !pasoDeHorma(p) && (
                          <div className="flex items-center gap-2 shrink-0">
                            {p.documento_file_id && puedeDescargar && (
                              <a href={fileUrl(p.documento_file_id)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1 hover:text-blue-700" data-testid={`ver-doc-${p.numero}`}>
                                <ExternalLink size={12} /> Ver
                              </a>
                            )}
                            {puede && (
                              <>
                                <button onClick={() => triggerUpload(`doc-${p.numero}`, "documento_paso", p.numero)} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-800" data-testid={`upload-doc-${p.numero}`}>
                                  <Upload size={12} /> {p.documento_file_id ? "Nueva versión" : "Subir"}
                                </button>
                                <input type="file" accept={(formatoDe(p.numero)?.formato_exts || []).map((e) => "." + e).join(",")} className="hidden" ref={(el) => (fileRefs.current[`doc-${p.numero}`] = el)} />
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {p.requiere_documento && !pasoDeHorma(p) && (
                        <div className="ml-8 mt-1.5">
                          <div className="flex items-center gap-3 text-[11px] text-slate-400 flex-wrap">
                            {formatoDe(p.numero)?.formato_label && (
                              <span>Formato: <span className="font-medium text-slate-500">{formatoDe(p.numero).formato_label}</span></span>
                            )}
                            {plantillaDe(p.numero) && puedeDescargar && (
                              <a href={fileUrl(plantillaDe(p.numero).id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 font-medium" data-testid={`plantilla-doc-${p.numero}`}>
                                <FileDown size={12} /> Descargar plantilla
                              </a>
                            )}
                          </div>
                          {versionesDe(p.numero).length > 0 && (
                            <div className="mt-1.5 border-l-2 border-slate-100 pl-3 space-y-1" data-testid={`versiones-doc-${p.numero}`}>
                              {versionesDe(p.numero).map((v) => (
                                <div key={v.id} className="flex items-center gap-2 text-[11px] text-slate-400">
                                  {puedeDescargar ? (
                                    <a href={fileUrl(v.id)} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-blue-600 truncate max-w-[180px]">{v.original_filename}</a>
                                  ) : (
                                    <span className="text-slate-500 truncate max-w-[180px]">{v.original_filename}</span>
                                  )}
                                  <span className="font-mono">v{v.version}</span>
                                  {v.is_current && <span className="text-emerald-600 font-semibold">Actual</span>}
                                  <span>· {v.uploaded_by_nombre} · {fechaHora(v.uploaded_at)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {!puede && <p className="text-[11px] text-slate-300 mt-2">Solo lectura — no es tu área.</p>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
